import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function getBusinessId(getState) {
  return getState().loginToken?.response?.user?.business_id;
}

function groupBaseUrl(getState, groupId) {
  const businessId = getBusinessId(getState);
  return `${ROOT_URL}/business/${businessId}/groups/${groupId}`;
}

function normalizeMailDomainStatusPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    registered: Boolean(raw.registered),
    mail_domain_verified: Boolean(raw.mail_domain_verified),
    mail_auth_domain: raw.mail_auth_domain ?? null,
    ticket_reply_host: raw.ticket_reply_host ?? null,
    sendgrid_inbound_parse_hostname: raw.sendgrid_inbound_parse_hostname ?? null,
  };
}

function emptyMailDomainStatus() {
  return {
    registered: false,
    mail_domain_verified: false,
    mail_auth_domain: null,
    ticket_reply_host: null,
    sendgrid_inbound_parse_hostname: null,
  };
}

export const fetchGroupMailDomainStatus = createAsyncThunk(
  "mailDomainAuthApi/fetchStatus",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${groupBaseUrl(getState, groupId)}/mail-domain-status`,
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ groupId, notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const fetchGroupMailDomainAuth = createAsyncThunk(
  "mailDomainAuthApi/fetch",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${groupBaseUrl(getState, groupId)}/mail-domain-auth`,
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ groupId, notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const registerGroupMailDomain = createAsyncThunk(
  "mailDomainAuthApi/register",
  async ({ groupId, domain }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.post(
        `${groupBaseUrl(getState, groupId)}/mail-domain-auth`,
        { domain: domain.trim() },
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const validateGroupMailDomain = createAsyncThunk(
  "mailDomainAuthApi/validate",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.post(
        `${groupBaseUrl(getState, groupId)}/mail-domain-auth/validate`,
        {},
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const deleteGroupMailDomainAuth = createAsyncThunk(
  "mailDomainAuthApi/delete",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.delete(
        `${groupBaseUrl(getState, groupId)}/mail-domain-auth`,
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

const initialState = {
  groupId: null,
  data: null,
  statusData: null,
  statusLoading: false,
  statusError: null,
  fetchLoading: false,
  registerLoading: false,
  validateLoading: false,
  deleteLoading: false,
  error: null,
  validateMessage: null,
  loaded: false,
};

const mailDomainAuthApi = createSlice({
  name: "mailDomainAuthApi",
  initialState,
  reducers: {
    clearGroupMailDomainAuth: () => initialState,
    clearGroupMailDomainError: (state) => {
      state.error = null;
    },
    clearGroupValidateMessage: (state) => {
      state.validateMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupMailDomainStatus.pending, (state) => {
        state.statusLoading = true;
        state.statusError = null;
      })
      .addCase(fetchGroupMailDomainStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.statusError = null;
        state.groupId = action.payload.groupId;
        const normalized = normalizeMailDomainStatusPayload(
          action.payload.payload?.data,
        );
        state.statusData = normalized ?? emptyMailDomainStatus();
      })
      .addCase(fetchGroupMailDomainStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.groupId = action.payload?.groupId ?? action.meta?.arg?.groupId ?? null;
        state.statusData = emptyMailDomainStatus();
        if (action.payload?.notFound || action.payload?.status === 404) {
          state.statusError = null;
        } else {
          state.statusError = action.payload;
        }
      })
      .addCase(fetchGroupMailDomainAuth.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
        state.loaded = false;
      })
      .addCase(fetchGroupMailDomainAuth.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.loaded = true;
        state.groupId = action.payload.groupId;
        state.data = action.payload.payload?.data ?? null;
        const normalized = normalizeMailDomainStatusPayload(state.data);
        state.statusData = normalized ?? emptyMailDomainStatus();
      })
      .addCase(fetchGroupMailDomainAuth.rejected, (state, action) => {
        state.fetchLoading = false;
        state.loaded = true;
        state.groupId = action.payload?.groupId ?? action.meta?.arg?.groupId ?? null;
        const p = action.payload;
        if (p?.notFound || p?.status === 404) {
          state.data = null;
          state.error = null;
          state.statusData = emptyMailDomainStatus();
        } else {
          state.error = p;
        }
      })
      .addCase(registerGroupMailDomain.pending, (state) => {
        state.registerLoading = true;
        state.error = null;
      })
      .addCase(registerGroupMailDomain.fulfilled, (state, action) => {
        state.registerLoading = false;
        state.groupId = action.payload.groupId;
        state.data = action.payload.payload?.data ?? state.data;
        const normalized = normalizeMailDomainStatusPayload(state.data);
        if (normalized) state.statusData = normalized;
      })
      .addCase(registerGroupMailDomain.rejected, (state, action) => {
        state.registerLoading = false;
        state.error = action.payload;
      })
      .addCase(validateGroupMailDomain.pending, (state) => {
        state.validateLoading = true;
        state.error = null;
        state.validateMessage = null;
      })
      .addCase(validateGroupMailDomain.fulfilled, (state, action) => {
        state.validateLoading = false;
        state.validateMessage = action.payload.payload?.message ?? null;
      })
      .addCase(validateGroupMailDomain.rejected, (state, action) => {
        state.validateLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteGroupMailDomainAuth.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteGroupMailDomainAuth.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.groupId = action.payload.groupId;
        state.data = null;
        state.validateMessage = null;
        state.statusData = emptyMailDomainStatus();
      })
      .addCase(deleteGroupMailDomainAuth.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearGroupMailDomainAuth,
  clearGroupMailDomainError,
  clearGroupValidateMessage,
} = mailDomainAuthApi.actions;

export default mailDomainAuthApi.reducer;
